import {
	ActionIcon,
	Avatar,
	Button,
	Drawer,
	List,
	Loader,
	Modal,
	Popover,
	TextInput
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import Link                                 from "next/link";
import { useEffect, useState }              from "react";

import { useGetDeviceType }      from "_common/hooks/global";
import { PrimaryButton }         from "_components/atoms/button";
import Icon                      from "_components/atoms/icon";
import Image                     from "_components/atoms/image";
import Render                    from "_components/atoms/render";
import Typography                from "_components/atoms/typography";
import FollowUser                from "_components/molecules/followUser";
import { useProModalScrollLock } from "_components/pages/proPage/utils";
import { getProCreators }        from "_services/api/pro";

import useStyles from "./styles";

const DropdownContent = ( { close, isMobile, from, searchEnabled = true, setPopover = () => { } } ) => {
	const [ searchTerm, setSearchTerm ] = useState ( "" );
	const [ debouncedSearchTerm ]       = useDebouncedValue ( searchTerm, 200 );
	const { classes, cx }               = useStyles ( {}, { name: "proChannelDropdown" } );
	const [ loading, setLoading ]       = useState ( true );

	const [ creatorsList, setCreatorsList ] = useState ( [] );

	const getProCreatorsData = async () => {
		setLoading ( true );
		try {
			const res = await getProCreators ( {
				follow: true
			} );

			setCreatorsList ( res?.data?.proCreators );
		} catch ( error ) {
			// do nothing
		}
		setLoading ( false );
	};

	useEffect ( () => {
		getProCreatorsData ();
	}, [] );

	const proStreamersList = creatorsList.filter ( item => item.name?.toLowerCase ().includes ( debouncedSearchTerm.toLowerCase () ) );

	const clearSearchTerm = () => setSearchTerm ( "" );

	return (

		<>
			<div className = { classes.headerWrapper }>
				<div className = { classes.headerTop }>
					{isMobile ? (
						<ActionIcon
							className = { classes.closeIconWrapper }
							onClick   = { close }
						>
							<Icon
								// modeType = { "dark" }
								name            = "backArrow"
								onClick         = { () => setPopover ( false ) }
								sameInBothTheme = { from === "profile" ? false : true }
								size            = { "2.5rem" }
								style           = { { margin: "0 1rem" } }
							/>
						</ActionIcon>
					) : null}

					<Typography className = { classes.popoverHeader }
						title                = "Pro Channels"
						weight               = { 500 }
					/>

					{!isMobile ? (
						<ActionIcon
							className = { classes.closeIconWrapper }
							onClick   = { close }
						>
							<Icon
								name            = { "cross" }
								sameInBothTheme = { from === "profile" ? false : true }
								size            = { "3rem" }
							/>
						</ActionIcon>
					) : null}
				</div>

				<TextInput
					autoComplete      = { "off" }
					classNames        = { {
						root    : classes.inputRoot,
						error   : classes.error,
						input   : classes.input,
						wrapper : classes.inputWrapper
					} }
					onChange          = { e => {
						setSearchTerm ( e.currentTarget.value );
					} }
					placeholder       = "Search streamer by name"
					rightSection      = { searchTerm ? (
						<ActionIcon onClick = { clearSearchTerm }>
							<Icon className = { classes.searchIcon }
								name           = "cross"
							>
							</Icon>
						</ActionIcon>
					) : (
						<ActionIcon>
							<Icon
								className       = { classes.searchIcon }
								name            = "search"
								sameInBothTheme = { from === "profile" ? false : true }
							>
							</Icon>
						</ActionIcon>
					) }
					rightSectionWidth = { "4rem" }
					value             = { searchTerm }
				/>
			</div>

			<div className = { classes.content }>

				<List classNames = { {
					root        : classes.listRoot,
					item        : classes.listItem,
					itemWrapper : classes.listItemWrapper
				} }
				>
					{proStreamersList.length > 0 ?
						proStreamersList.map ( ( creator, index ) => {
							return (
								<List.Item key = { index }
									onClick       = { close }
								>
									<Link href = { creator.id ? `/profile/${ creator.id }` : "/" }>
										<div className = { classes.listItemContent }>
											<Image
												className = { classes.userImg }
												src       = { creator.photo }
											/>

											<div className = { classes.listItemRight }>
												<div className = { classes.nameWrapper }>

													<Typography
														truncate
														className = { classes.listText }
														size      = "sm"
														title     = { creator.name }
													/>

													{creator.isCeleb ? (
														<Icon sameInBothTheme
															name = "verifiedProfile"
															size = { 18 }
														/>
													) : null}
												</div>

												<Render condition = { from === "profile" }>
													<div className = { classes.followBtnWrapper }
														onClick       = { e => e.preventDefault () }
													>

														<FollowUser
															followComponent   = { (
																<PrimaryButton
																	className = { classes.followBtn }
																	size      = "small"
																	title     = { "+ Follow" }
																/>
															) }
															isFollowing       = { creator.isFollowing }
															unfollowComponent = { (
																<PrimaryButton
																	className = { cx ( classes.followBtn, classes.unfollow ) }
																	size      = "small"
																	title     = { "Following" }
																/>
															) }
															userId            = { creator.id }
															userName          = { creator.name }
														/>

													</div>
												</Render>
											</div>
										</div>

									</Link>
								</List.Item>
							);
						} ) : (
							<div className = { classes.loaderWrapper }>
								<Loader size = { "xl" } />
							</div>
						)}
				</List>
			</div>
		</>
	);

};

function component ( props ) {
	const { TargetComponent, popoverOpened, setPopover, title } = props;
	const { from }                                              = props;
	const [ opened, { open, close, toggle } ]                   = useDisclosure ();

	const viewAllClick = () => {
		if ( window.rooterProWebInterface )
			window.rooterProWebInterface.viewAllProChannels ( toggle );
		else
			toggle ();
	};

	const deviceType = useGetDeviceType ();
	const isMobile   = deviceType === "mobile";

	useProModalScrollLock ( opened );

	if ( isMobile ) {
		return (
			<MobileComponent
				close           = { close }
				from            = { from }
				opened          = { opened || popoverOpened }
				setPopover      = { setPopover }
				TargetComponent = { TargetComponent }
				title           = { title }
				toggle          = { viewAllClick }
			/>
		);
	}

	return (
		<DesktopComponent
			close           = { close }
			from            = { from }
			opened          = { opened }
			setPopOver      = { setPopover }
			TargetComponent = { TargetComponent }
			title           = { title }
			toggle          = { viewAllClick }
		/>
	);

}

export default component;

function DesktopComponent ( {
	from,
	toggle,
	opened,
	setPopover,
	close,
	TargetComponent,
	title
} ) {

	const { classes } = useStyles ( { from }, { name: "proChannelDropdown" } );

	if ( TargetComponent ) {
		return (
			<>
				{/* <Button
					onClick = { toggle }
					style = { { all: "unset" } }
				> */}

				<span onClick = { toggle }>
					<TargetComponent />
				</span>

				{/* </Button> */}

				<Modal
					centered
					classNames          = { {
						content : classes.popoverDropdown,
						body    : classes.modalBody
					} }
					closeOnClickOutside = { true }
					offset              = { {
						mainAxis  : -10,
						crossAxis : 50
					} }
					onClose             = { toggle }
					opened              = { opened }
					withCloseButton     = { false }
					withinPortal        = { true }
					zIndex              = { 1500 }
				>
					<DropdownContent
						close      = { close }
						from       = { from }
						setPopover = { setPopover }
					/>
				</Modal>
			</>
		);
	}

	return (
		<Popover
			classNames          = { {
				dropdown: classes.popoverDropdown
			} }
			closeOnClickOutside = { true }
			offset              = { {
				mainAxis  : -10,
				crossAxis : 50
			} }
			onChange            = { toggle }
			opened              = { opened }
			position            = { "bottom-start" }
			transitionProps     = { {
				transition: "pop-top-left"
			} }
			withinPortal        = { true }
			zIndex              = { 1500 }
		>
			<Popover.Target>
				<div>
					<ViewAllChip title       = { title }
						toggle                  = { toggle }
					/>
				</div>
			</Popover.Target>

			<Popover.Dropdown>
				<div>
					<DropdownContent
						close      = { close }
						from       = { from }
						setPopover = { setPopover }
					/>
				</div>
			</Popover.Dropdown>
		</Popover>
	);
}
const ViewAllChip = ( { toggle, title } ) => {
	const { classes, cx, theme } = useStyles ( {}, { name: "viewAllCHip" } );

	return (
		<Avatar
			classNames = { {
				root        : cx ( classes.avatar, classes.viewAllChip ),
				placeholder : classes.avatarPlaceholder
			} }
			onClick    = { toggle }
			size       = { "3.6rem" }
		>
			<Typography
				gradient = { {
					from : theme.other.paletteNew.aquaBlue,
					to   : theme.other.paletteNew.softBlue,
					deg  : 45
				} }
				size     = { "xs" }
				title    = { title || "View All" }
				variant  = "gradient"
				weight   = { "normal" }
			/>

			<Icon className  = { classes.caretIcon }
				name            = { "arrow" }
				sameInBothTheme = { true }
				size            = { "1.6rem" }
			/>

		</Avatar>
	);
};

function MobileComponent ( {
	from,
	toggle,
	opened,
	setPopover,
	close,
	TargetComponent,
	title
} ) {

	const { classes } = useStyles ( undefined, { name: "avataar-123" } );

	return (
		<>
			{TargetComponent || (
				<ViewAllChip title      = { title }
					toggle                 = { toggle }
				/>
			)}

			<Drawer
				classNames      = { { body: classes.drawerBody } }
				onClose         = { close }
				opened          = { opened }
				position        = "bottom"
				size            = { "100%" }
				withCloseButton = { false }
				zIndex          = { 1500 }
			>
				<DropdownContent
					close      = { close }
					from       = { from }
					isMobile   = { true }
					setPopover = { setPopover }
				/>
			</Drawer>
		</>
	);
}
